import { supabase } from '@/integrations/supabase/client';

/**
 * Enhanced security wrapper for authentication operations
 * Simplified auth flow without logging functions
 */
export const SecurityEnhancedAuth = {
  async signInWithPassword(email: string, password: string) {
    try {
      // Development only logging
      
      // Enhanced input validation
      if (!email || email.length > 254) {
        throw new Error('Invalid email address');
      }
      
      if (!password || password.length < 8) {
        throw new Error('Password must be at least 8 characters');
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        // Log errors for debugging
        throw error;
      }
      
      // Login successful
      if (data.user) {
        // User authenticated successfully
      }
      
      return { data, error: null };
      
    } catch (error: any) {
      // Error handling for authentication
      return { data: null, error };
    }
  },

  async signUp(email: string, password: string, userData?: any) {
    try {
      // Processing signup request
      
      // Enhanced input validation before any API calls
      if (!email || email.length > 254) {
        throw new Error('Invalid email address');
      }
      
      if (!password || password.length < 8 || password.length > 128) {
        throw new Error('Password must be between 8 and 128 characters');
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData || {},
          emailRedirectTo: `${window.location.origin}/`
        }
      });
      
      if (error) {
        // Error during signup
        throw error;
      }
      
      // Signup successful
      if (data.user) {
        // Signup completed successfully
      }
      
      return { data, error: null };
      
    } catch (error: any) {
      // Signup error handling
      return { data: null, error };
    }
  },

  async resetPassword(email: string) {
    try {
      // Processing password reset
      
      // Enhanced email validation
      if (!email || email.length > 254) {
        throw new Error('Invalid email address');
      }
      
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?mode=reset`,
      });
      
      if (error) {
        // Password reset error
        throw error;
      }
      
      return { data, error: null };
      
    } catch (error: any) {
      // Password reset failed
      return { data: null, error };
    }
  },

  async updatePassword(newPassword: string) {
    try {
      // Processing password update
      
      // Enhanced password validation
      if (!newPassword || newPassword.length < 8 || newPassword.length > 128) {
        throw new Error('Password must be between 8 and 128 characters');
      }
      
      // Check for password complexity
      const hasUpperCase = /[A-Z]/.test(newPassword);
      const hasLowerCase = /[a-z]/.test(newPassword);
      const hasNumbers = /\d/.test(newPassword);
      
      if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
        throw new Error('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      }
      
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        // Password update error
        throw error;
      }
      
      // Password update successful
      if (data.user) {
        // Password updated successfully
      }
      
      return { data, error: null };
      
    } catch (error: any) {
      // Password update failed
      return { data: null, error };
    }
  },

  async signOut() {
    try {
      // Processing signout
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        // Signout error
        throw error;
      }
      
      return { error: null };
      
    } catch (error: any) {
      // Signout failed
      return { error };
    }
  }
};